import type { FC, ReactNode } from 'react';
import type { SyncStatus } from '../../services/syncService';

const STATUS_BADGE: Record<SyncStatus, { label: string; className: string }> = {
    PENDING: { label: 'Pending', className: 'bg-stone-100 text-stone-600 ring-stone-200' },
    IN_PROGRESS: { label: 'Running', className: 'bg-blue-50 text-blue-700 ring-blue-200' },
    SUCCEEDED: { label: 'Succeeded', className: 'bg-[#f0f7e6] text-[#5a7f28] ring-[#c5e08a]' },
    FAILED: { label: 'Failed', className: 'bg-rose-50 text-rose-700 ring-rose-200' },
    CANCELLED: { label: 'Cancelled', className: 'bg-amber-50 text-amber-700 ring-amber-200' },
};

export const StatusBadge: FC<{ status: SyncStatus }> = ({ status }) => {
    const badge = STATUS_BADGE[status] ?? {
        label: status,
        className: 'bg-stone-100 text-stone-600 ring-stone-200',
    };
    return (
        <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${badge.className}`}>
            {badge.label}
        </span>
    );
};

export const TriggerBadge: FC<{ trigger: string }> = ({ trigger }) => (
    <span className="inline-flex items-center rounded-full bg-stone-100 px-2 py-0.5 text-xs font-medium text-stone-600 ring-1 ring-stone-200">
        {trigger === 'CRON' ? 'Scheduled' : 'By hand'}
    </span>
);

export const Panel: FC<{ title: string; description?: string; children: ReactNode }> = ({
    title,
    description,
    children,
}) => (
    <div className="rounded-2xl border border-stone-200 bg-white shadow-sm overflow-hidden">
        <div className="h-1 bg-[#ee2b74]" />
        <div className="px-6 py-4 border-b border-stone-200 bg-stone-50/60">
            <h4 className="text-sm font-semibold text-stone-900">{title}</h4>
            {description && <p className="text-xs text-stone-500 mt-1">{description}</p>}
        </div>
        {children}
    </div>
);

export const Table: FC<{ headers: string[]; children: ReactNode }> = ({ headers, children }) => (
    <div className="overflow-auto">
        <table className="min-w-full text-sm">
            <thead className="bg-white border-b border-stone-200">
                <tr>
                    {headers.map((h) => (
                        <th
                            key={h}
                            className="py-3 px-6 text-left text-xs font-semibold uppercase tracking-wide text-stone-500 whitespace-nowrap"
                        >
                            {h}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">{children}</tbody>
        </table>
    </div>
);

export const ErrorBanner: FC<{ message: string }> = ({ message }) => (
    <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-800">
        {message}
    </div>
);

/**
 * A count that is only worth noticing when it is not zero — unmatched rows, products
 * taken off sale. Zero is the quiet everyday case and should read as such.
 */
export const Notable: FC<{ value: number; tone?: 'warn' | 'info' }> = ({ value, tone = 'warn' }) => {
    if (!value) return <span className="text-stone-400">0</span>;
    const className = tone === 'warn' ? 'text-amber-700 font-medium' : 'text-blue-700 font-medium';
    return <span className={className}>{value.toLocaleString()}</span>;
};

/**
 * BUSY, SKIPPED and IN_PROGRESS are ordinary answers, not faults — the scheduled job
 * may simply have got there first. They read as information, not as errors.
 */
export const NoticeBanner: FC<{ message: string; onDismiss: () => void }> = ({ message, onDismiss }) => (
    <div className="rounded-2xl border border-blue-200 bg-blue-50 px-5 py-4 flex items-start justify-between gap-4">
        <p className="text-sm text-blue-800">{message}</p>
        <button
            type="button"
            onClick={onDismiss}
            className="text-xs font-medium text-blue-700 hover:text-blue-900 flex-shrink-0"
        >
            Dismiss
        </button>
    </div>
);

export const RunButton: FC<{
    onClick: () => void;
    busy: boolean;
    disabled?: boolean;
    label?: string;
    busyLabel?: string;
    title?: string;
}> = ({ onClick, busy, disabled, label = 'Run now', busyLabel = 'Running…', title }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={busy || disabled}
        aria-busy={busy}
        title={title}
        className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#ee2b74] px-4 py-2 text-sm font-semibold text-white shadow-sm shadow-pink-200 hover:bg-[#d4245f] transition-colors disabled:cursor-not-allowed disabled:opacity-60"
    >
        {busy ? busyLabel : label}
    </button>
);
